
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

export interface UpcomingPayment {
  subscription_id: string;
  rental_id: string;
  human_number?: string;
  courier_id: string;
  courier_name?: string;
  amount: number;
  next_payment_date: string;
  status: string;
  interval: string;
  days_until_payment: number;
  payment_method?: string;
}

/**
 * Получает данные о предстоящих платежах напрямую через Supabase клиент
 * @param daysLimit - Лимит дней для платежей (по умолчанию 14 дней)
 * @returns Promise с данными о предстоящих платежах
 */
export const fetchUpcomingPayments = async (daysLimit: number = 14): Promise<UpcomingPayment[]> => {
  try {
    console.log('Выполняется запрос предстоящих платежей с лимитом дней:', daysLimit);
    
    // Получаем подписки с необходимыми связанными данными
    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        id:id,
        rental_id,
        amount,
        next_payment_date,
        status,
        interval,
        payment_method_id,
        rentals:rentals!rental_id (
          human_number,
          courier_id,
          couriers:couriers!courier_id (
            id,
            name,
            payment_method_id
          )
        )
      `)
      .eq('status', 'active')
      .not('next_payment_date', 'is', null)
      .gte('next_payment_date', new Date().toISOString().split('T')[0])
      .lt('next_payment_date', new Date(Date.now() + daysLimit * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
      .order('next_payment_date', { ascending: true });
    
    if (error) {
      console.error('Ошибка при получении данных о предстоящих платежах:', error);
      toast.error('Ошибка при получении данных о платежах', {
        description: error.message
      });
      throw error;
    }
    
    if (!data || !Array.isArray(data)) {
      console.error('Неверный формат данных:', data);
      toast.error('Неверный формат данных с сервера');
      return [];
    }
    
    // Преобразуем данные в нужный формат
    const payments: UpcomingPayment[] = data.map(item => {
      // Расчет количества дней до платежа
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      const paymentDate = new Date(item.next_payment_date);
      paymentDate.setHours(0, 0, 0, 0);
      
      const diffTime = paymentDate.getTime() - currentDate.getTime();
      const days_until_payment = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      // Правильно обрабатываем данные аренды и курьера
      const rentalData = Array.isArray(item.rentals) ? item.rentals[0] : item.rentals;
      const courierData = rentalData?.couriers;
      
      // Обрабатываем курьерские данные правильно
      let courierName = 'Неизвестно';
      if (courierData) {
        if (Array.isArray(courierData) && courierData.length > 0) {
          courierName = courierData[0]?.name || 'Неизвестно';
        } else if (typeof courierData === 'object' && 'name' in courierData && courierData.name) {
          courierName = String(courierData.name);
        }
      }
      
      return {
        subscription_id: item.id,
        rental_id: item.rental_id,
        human_number: rentalData?.human_number,
        courier_id: rentalData?.courier_id || '',
        courier_name: courierName,
        amount: item.amount,
        next_payment_date: item.next_payment_date,
        status: item.status,
        interval: item.interval,
        days_until_payment,
        payment_method: item.payment_method_id === 'card' ? 'card' : 'sbp'
      };
    });
    
    console.log('Получены данные о предстоящих платежах:', payments.length || 0, 'записей');
    return payments;
  } catch (error: any) {
    console.error('Ошибка при получении данных о предстоящих платежах:', error);
    toast.error('Не удалось загрузить данные о платежах', {
      description: error.message || 'Неизвестная ошибка'
    });
    return [];
  }
};

/**
 * Получает количество предстоящих платежей на текущую неделю
 * @returns Promise с количеством платежей
 */
export const fetchUpcomingPaymentsCount = async (): Promise<number> => {
  try {
    const { count, error } = await supabase
      .from('subscriptions')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'active')
      .not('next_payment_date', 'is', null)
      .gte('next_payment_date', new Date().toISOString().split('T')[0])
      .lt('next_payment_date', new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    
    if (error) {
      console.error('Ошибка при получении количества предстоящих платежей:', error);
      throw error;
    }
    
    return count || 0;
  } catch (error) {
    console.error('Ошибка при получении количества предстоящих платежей:', error);
    return 0;
  }
};
